import { Fragment } from 'react';
import {
  Grid,
  Box,
  Typography,
  Table,
  TableBody,
  TableRow,
  TableCell,
  RadioGroup,
  Radio,
  useTheme,
} from '@mui/material';
import SectionTitle from '../../components/SectionTitle';
import { IFormValues, IFormWatch } from 'routes/Main/type';
import useTableForm from '../../hooks/useTableForm';
import MuiRadioGroupSub from './components/MuiRadioGroupSub';

interface Props extends IFormValues, IFormWatch {
  disabled?: boolean;
}

const NDIContents = (props: Props) => {
  const { palette } = useTheme();

  const { radioGroup, sumValues } = useTableForm(props);
  const { disabled, watch, setValue, getValues } = props;

  const answers = [
    {
      id: 'ndi01',
      title: '第1部分 - 疼痛强度',
      desc: [
        '我现在完全不疼。',
        '我现在只有轻微的疼痛。',
        '我现在有中等程度的疼痛。',
        '我现在有相当严重的疼痛。',
        '我现在有非常严重的疼痛。',
        '我现在感受到无法想象的最剧烈疼痛。',
      ],
    },
    {
      id: 'ndi02',
      title: '第2部分 - 个人护理（洗漱、穿衣等）',
      desc: [
        '我可以正常照顾自己，不会引起额外的疼痛。',
        '我可以正常照顾自己，但会引起额外的疼痛。',
        '照顾自己会感到疼痛，我会动作缓慢且小心。',
        '我需要一些帮助，但大部分个人护理可以自己完成。',
        '我每天大部分时间都需要他人帮助来进行自我照顾。',
        '我不穿衣，洗漱困难并整天躺在床上。',
      ],
    },
    {
      id: 'ndi03',
      title: '第3部分 - 提举重物',
      desc: [
        '我可以提举重物，并且不会感到额外疼痛。',
        '我可以提举重物，但会引起额外疼痛。',
        '疼痛使我无法从地上提起重物，但如果重物放在方便的地方，例如桌上，我可以应付。',
        '疼痛使我无法提起重物，但如果是轻到中等重量且摆放方便，我可以应付。',
        '我只能提起非常轻的物品。',
        '我完全无法提举或搬运任何东西。',
      ],
    },
    {
      id: 'ndi04',
      title: '第4部分 - 阅读',
      desc: [
        '我可以阅读任意时间，脖子不会感到疼痛。',
        '我可以阅读任意时间，脖子有轻微疼痛。',
        '我可以阅读任意时间，脖子有中度疼痛。',
        '由于脖子有中度疼痛，我无法阅读太久。',
        '由于脖子严重疼痛，我几乎无法阅读。',
        '我完全无法阅读。',
      ],
    },
    {
      id: 'ndi05',
      title: '第5部分 - 头痛',
      desc: [
        '我完全没有头痛。',
        '我偶尔会有轻微的头痛。',
        '我偶尔会有中度头痛。',
        '我经常会有中度头痛。',
        '我经常会有严重的头痛。',
        '我几乎一直都在头痛。',
      ],
    },
    {
      id: 'ndi06',
      title: '第6部分 - 注意力集中',
      desc: [
        '我想集中注意力时完全没有困难。',
        '我想集中注意力时有轻微困难。',
        '我想集中注意力时有一定程度的困难。',
        '我想集中注意力时有很大的困难。',
        '我想集中注意力时有极大的困难。',
        '我完全无法集中注意力。',
      ],
    },
    {
      id: 'ndi07',
      title: '第7部分 - 工作',
      desc: [
        '我可以做任何我想做的工作。',
        '我只能做我平时的工作，不能再多做。',
        '我可以做大部分平时的工作，但不能再多做。',
        '我无法从事我平时的工作。',
        '我几乎无法工作。',
        '我完全无法工作。',
      ],
    },
    {
      id: 'ndi08',
      title: '第8部分 - 驾驶',
      desc: [
        '我开车时脖子完全不痛。',
        '我可以开车任意时间，脖子有轻微疼痛。',
        '我可以开车任意时间，脖子有中度疼痛。',
        '由于脖子中度疼痛，我无法长时间开车。',
        '由于脖子严重疼痛，我几乎无法开车。',
        '我完全无法开车。',
      ],
    },
    {
      id: 'ndi09',
      title: '第9部分 - 睡眠',
      desc: [
        '我睡觉没有任何困难。',
        '我的睡眠稍有影响（失眠少于1小时）。',
        '我的睡眠有轻微影响（失眠1-2小时）。',
        '我的睡眠中度受影响（失眠2-3小时）。',
        '我的睡眠严重受影响（失眠3-5小时）。',
        '我的睡眠完全受影响（失眠5-7小时）。',
      ],
    },
    {
      id: 'ndi10',
      title: '第10部分 - 休闲活动',
      desc: [
        '我可以参与所有休闲活动，脖子完全不痛。',
        '我可以参与所有休闲活动，虽然脖子有些痛。',
        '由于脖子疼痛，我可以参与大部分但不是全部的休闲活动。',
        '由于脖子疼痛，我只能参与少数几项休闲活动。',
        '由于脖子疼痛，我几乎无法参与任何休闲活动。',
        '我完全无法参与任何休闲活动。',
      ],
    },
  ];

  const watchSumValues = () => {
    const values = answers.map(({ id }) => {
      if (props.watch(`${id}`) === undefined) {
        return 0;
      } else {
        return Number(props.watch(`${id}`));
      }
    });
    return sumValues(values);
  };

  // console.log('기본값', Number(getValues('ndi10')));

  return (
    <Fragment>
      <Typography sx={{ fontSize: '14px', margin: '40px 0 -10px 25px' }}>
        本问卷旨在了解您的颈部疼痛如何影响您在日常生活中的管理能力。
        请回答每一部分，并在每部分中仅标记适合您的一个选项。我们意识到您可能认为某一部分的两个或多个陈述与您相关，但请仅标记最符合您问题的选项。
      </Typography>
      {answers.map(
        (answer: { id: string; title: string; desc: string[] }, idx) => (
          <>
            <SectionTitle title={answer.title} />
            <Grid item xs={12}>
              <RadioGroup
                name={answer.title}
                defaultValue={Number(getValues(`answer.id`))}
              >
                <Table aria-label="simple table">
                  <TableBody>
                    {answer.desc.map((v, i) => {
                      return (
                        <TableRow>
                          <TableCell
                            sx={{
                              width: '80%',
                            }}
                          >
                            {v}
                          </TableCell>
                          <TableCell sx={{ width: '10%' }}>{`${i}`}</TableCell>
                          <TableCell>
                            <MuiRadioGroupSub
                              disabled={disabled}
                              name={`${answer.title}`}
                              values={[i]}
                              value={Number(getValues(`${answer.id}`))}
                              defaultValue={Number(getValues(`${answer.id}`))}
                              onChange={() => {
                                setValue(`${answer.id}`, i);
                              }}
                            />
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </RadioGroup>
            </Grid>
          </>
        )
      )}

      <Grid item xs={12}>
        <Box display={'flex'} flexDirection={'column'} alignItems={'flex-end'}>
          <Typography
            gutterBottom
            minWidth={115}
            fontWeight={700}
            variant="subtitle1"
          >
            总分 : {watchSumValues()} / 50
          </Typography>
          <Typography
            minWidth={115}
            variant="caption"
            sx={{ color: `${palette.primary.main}`, marginTop: '30px' }}
          >
            <Typography variant="inherit">
              <Box component={'strong'} mr={0.5}>
                0-4
              </Box>
              没有残疾
            </Typography>
            <Typography variant="inherit">
              <Box component={'strong'} mr={0.5}>
                5-14
              </Box>
              轻度残疾
            </Typography>
            <Typography variant="inherit">
              <Box component={'strong'} mr={0.5}>
                15-24
              </Box>
              中度残疾
            </Typography>
            <Typography variant="inherit">
              <Box component={'strong'} mr={0.5}>
                25-34
              </Box>
              重度残疾
            </Typography>
            <Typography variant="inherit">
              <Box component={'strong'} mr={0.5}>
                35-50
              </Box>
              完全残疾
            </Typography>
          </Typography>
        </Box>
      </Grid>
    </Fragment>
  );
};

export default NDIContents;
